import React, { useState, useCallback, useRef, useEffect, DragEvent, ChangeEvent } from "react";
import { Button, Tooltip, Spinner } from "@fluentui/react-components";
import { Attach24Regular, DocumentAdd24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";

import { UploadedFile, UploadFileResult, uploadFileApi, listUploadedFilesApi } from "../../api";
import { useLogin, getToken } from "../../authConfig";
import styles from "./UploadFile.module.css";

interface Props {
    className?: string;
    disabled?: boolean;
    maxSizeMb: number;
    conversationId: string;
}

type FileUploadStatus = {
    file: File;
    status: "pending" | "uploading" | "success" | "failed";
    message?: string;
};

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".txt", ".md", ".csv"];

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFiles(files: File[], maxSizeMb: number): { valid: File[]; rejected: FileUploadStatus[] } {
    const valid: File[] = [];
    const rejected: FileUploadStatus[] = [];
    for (const f of files) {
        const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            rejected.push({ file: f, status: "failed", message: `File type '${ext}' not allowed.` });
        } else if (f.size > maxSizeMb * 1024 * 1024) {
            rejected.push({ file: f, status: "failed", message: `Exceeds ${maxSizeMb} MB limit.` });
        } else {
            valid.push(f);
        }
    }
    return { valid, rejected };
}

export const UploadFile: React.FC<Props> = ({ className, disabled, maxSizeMb, conversationId }: Props) => {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [isListLoading, setIsListLoading] = useState<boolean>(false);
    const [uploadQueue, setUploadQueue] = useState<FileUploadStatus[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    if (!useLogin) {
        throw new Error("The UploadFile component requires useLogin to be true");
    }

    const client = useMsal().instance;

    const refreshFileList = useCallback(
        async (idToken: string) => {
            setIsListLoading(true);
            try {
                const files = await listUploadedFilesApi(idToken, conversationId);
                setUploadedFiles(files);
            } finally {
                setIsListLoading(false);
            }
        },
        [conversationId]
    );

    const openDialog = useCallback(async () => {
        setIsDialogOpen(true);
        setUploadQueue([]);
        try {
            const idToken = await getToken(client);
            if (!idToken) throw new Error("No auth token");
            await refreshFileList(idToken);
        } catch (e) {
            console.error(e);
            setIsListLoading(false);
        }
    }, [client, refreshFileList]);

    // When the active conversation changes while the dialog is open, refresh the list
    // so the user always sees files attached to the current conversation.
    useEffect(() => {
        if (!isDialogOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const idToken = await getToken(client);
                if (!idToken || cancelled) return;
                await refreshFileList(idToken);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [conversationId, isDialogOpen, client, refreshFileList]);

    const closeDialog = useCallback(() => setIsDialogOpen(false), []);

    const handleUploadFiles = useCallback(
        async (files: File[]) => {
            const { valid, rejected } = validateFiles(files, maxSizeMb);

            const initial: FileUploadStatus[] = [
                ...rejected,
                ...valid.map(f => ({ file: f, status: "uploading" as const }))
            ];
            setUploadQueue(initial);

            if (valid.length === 0) return;

            try {
                const idToken = await getToken(client);
                if (!idToken) throw new Error("No auth token");

                const formData = new FormData();
                for (const f of valid) {
                    formData.append("file", f);
                }

                const response = await uploadFileApi(formData, idToken, conversationId);

                setUploadQueue(prev =>
                    prev.map(entry => {
                        const result: UploadFileResult | undefined = response.files.find(r => r.filename === entry.file.name);
                        if (!result) return entry;
                        return { ...entry, status: result.status === "success" ? "success" : "failed", message: result.message };
                    })
                );

                await refreshFileList(idToken);
            } catch (e: any) {
                setUploadQueue(prev =>
                    prev.map(entry =>
                        entry.status === "uploading" ? { ...entry, status: "failed", message: String(e?.message ?? "Upload error") } : entry
                    )
                );
            }
        },
        [client, conversationId, refreshFileList]
    );

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };
    const handleDragLeave = () => setIsDragOver(false);
    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const dropped = Array.from(e.dataTransfer.files);
        if (dropped.length) handleUploadFiles(dropped);
    };
    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        handleUploadFiles(Array.from(e.target.files));
        e.target.value = "";
    };

    return (
        <div className={`${styles.container} ${className ?? ""}`}>
            <Tooltip content={t("upload.conversationFiles")} relationship="label">
                <Button
                    appearance="subtle"
                    icon={<Attach24Regular />}
                    disabled={disabled}
                    aria-label={t("upload.conversationFiles")}
                    onClick={openDialog}
                />
            </Tooltip>

            {isDialogOpen && (
                <>
                    {/* Backdrop — click to close */}
                    <div className={styles.backdrop} onClick={closeDialog} aria-hidden="true" />

                    {/* Modal panel */}
                    <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t("upload.conversationFiles")}>
                        <div className={styles.modalHeader}>
                            <span className={styles.modalTitle}>{t("upload.conversationFiles")}</span>
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                onClick={closeDialog}
                                aria-label={t("upload.closeDialog")}
                            />
                        </div>

                        <div className={styles.modalBody}>
                            {/* Drag-and-drop / click-to-browse zone */}
                            <div
                                className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ""}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                role="button"
                                aria-label={t("upload.dropZoneLabel")}
                                tabIndex={0}
                                onKeyDown={e => e.key === "Enter" && fileInputRef.current?.click()}
                            >
                                <DocumentAdd24Regular />
                                <span>{t("upload.dropZoneHint")}</span>
                                <span className={styles.dropZoneSubtext}>
                                    {t("upload.allowedTypes")}: .pdf, .docx, .txt, .md, .csv &mdash; {t("upload.maxSize")}: {maxSizeMb} MB
                                </span>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".pdf,.docx,.txt,.md,.csv"
                                className={styles.hiddenInput}
                                onChange={handleFileInputChange}
                            />

                            {/* Per-file upload status */}
                            {uploadQueue.length > 0 && (
                                <div className={styles.uploadQueue}>
                                    {uploadQueue.map((entry, i) => (
                                        <div key={i} className={styles.uploadQueueItem}>
                                            <span className={styles.uploadQueueName}>{entry.file.name}</span>
                                            {entry.status === "uploading" && <Spinner size="tiny" />}
                                            {entry.status === "success" && (
                                                <span className={styles.statusSuccess}>{t("upload.uploadSuccess")}</span>
                                            )}
                                            {(entry.status === "failed" || entry.status === "pending") && (
                                                <span className={styles.statusFailed}>{entry.message ?? t("upload.uploadedFileError")}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Existing file list */}
                            <h3 className={styles.fileListHeading}>{t("upload.conversationFilesLabel")}</h3>
                            {isListLoading && <Spinner size="small" />}
                            {!isListLoading && uploadedFiles.length === 0 && <span>{t("upload.noFilesInConversation")}</span>}
                            {uploadedFiles.map((file, index) => (
                                <div key={index} className={styles.fileRow}>
                                    <div className={styles.fileInfo}>
                                        <span className={styles.fileName}>{file.filename}</span>
                                        <span className={styles.fileMeta}>
                                            {formatBytes(file.size)} &middot; {new Date(file.upload_date).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.modalFooter}>
                            <Button appearance="secondary" onClick={closeDialog}>
                                {t("upload.closeDialog")}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
